package Structs.Api

import Structs.ClientTaxNoteData
import Utils.FillTaxNoteType
import Utils.getJsonWithConfig
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import myName

@myName("ApiConfig")
@Serializable
data class ApiConfig(
    val storeNextNote: Int,
    val storeNextTax: Int,
    val managerOrder: Int,
    val managerNote: Int,
    val managerTax: Int,
    val oneNote: Int,
    val oneOrder: Int,
    val oneTax: Int,
    val hashavshevetNote: Int,
    val hashavshevetTax: Int,
    val read_products: Int,
    val read_customers: Int,
    val read_prices: Int, // 0 - no read and send, 1 - read and send, 2 - dont read dont send, 3 - read and dont send
    val mailOrder: Int = 0,
    val mailTaxNote: Int = 0,
    val mailNote: Int = 0,
    val read_orders: String = "[]",
    val whatsapp:String = "{}",
    /**
     * ai_assistent platform (embed + `/ai/complete`, text-to-data, debt automation).
     * JSON string — same pattern as [whatsapp]. Shape (nexilcolab `getAiConfig()`):
     *
     * ```json
     * {
     *   "default": {
     *     "company": "alkimaycompany",
     *     "baseUrl": "https://ai.solvitcompany.com",
     *     "apiBase": "https://ai.solvitcompany.com",
     *     "token": "nk_live_…",
     *     "phone": true
     *   },
     *   "users": { "yotam": "nk_live_…", "moshe": { "token": "nk_live_…", "phone": true } }
     * }
     * ```
     *
     * `phone: true` enables AI draft entry points on the Android app (per-user
     * override beats default). Omitted or false → hidden on phone.
     * (ai_assistent admin) — never in this JSON.
     */
    val ai: String = "{}",
    /** SMTP or Chatwoot email channel (`server` 11); separate from [whatsapp]. */
    val email: String = "{}",
    val last_counter:String = "{}",
    /**
     * Hosted payment provider. JSON string pasted into `api_config.credit`.
     * Omit `provider` (or set `"tranzila"`) for the existing Tranzila iframe.
     * `"payplus"` uses a PayPlus payment page; the client still receives
     * `https://nihulkav.online/pay/s/<session>`.
     *
     * Tranzila:
     * ```json
     * {
     *   "terminal_name": "TOKEN_TERMINAL",
     *   "iframe_terminal_name": "IFRAME_TERMINAL",
     *   "app_key": "APP_KEY",
     *   "secret": "SECRET"
     * }
     * ```
     *
     * PayPlus:
     * ```json
     * {
     *   "provider": "payplus",
     *   "api_key": "API_KEY",
     *   "secret_key": "SECRET_KEY",
     *   "payment_page_uid": "PAYMENT_PAGE_UID",
     *   "terminal_uid": "TERMINAL_UID",
     *   "cashier_uid": "CASHIER_UID",
     *   "sandbox": false
     * }
     * ```
     *
     * `payment_page_uid` is the hosted page. `terminal_uid` and `cashier_uid`
     * are used only when charging a saved PayPlus token.
     */
    val credit:String="{}",

    ) {
    val readOrderJson: List<ReadOrderJson> = ReadOrderJson.createFromJson(read_orders)
    val whatsappJson = getJsonWithConfig().parseToJsonElement(whatsapp)
    val emailJson = getJsonWithConfig().parseToJsonElement(email)
    val lastCounterJson = getJsonWithConfig().parseToJsonElement(last_counter)
    @myName("hasManager")
    fun hasManager(): Boolean {
        return managerNote == 1 || managerTax == 1 || managerOrder == 1
    }

    @myName("hasOne")
    fun hasOne(): Boolean {
        return oneNote == 1 || oneTax == 1 || oneOrder == 1
    }
    @myName("hasHishuvit")
    fun hasHishuvit(): Boolean {
        return oneNote == 2 || oneTax == 2 || oneOrder == 2
    }
    @myName("hasPriority")
    fun hasPriority(): Boolean {
        return oneNote == 3 || oneTax == 3 || oneOrder == 3
    }

    @myName("hasPriorityPremium")
    fun hasPriorityPremium(): Boolean {
        return oneNote == 5 || oneTax == 5 || oneOrder == 5
    }
    @myName("hasLesser")
    fun hasLesser(): Boolean {
        return oneNote == 4 || oneTax == 4 || oneOrder == 4
    }

    @myName("hasStoreNext")
    fun hasStoreNext(): Boolean {
        return storeNextTax == 1 || storeNextNote == 1
    }

    @myName("hasHashavshevet")
    fun hasHashavshevet(): Boolean {
        return hashavshevetNote >= 1 || hashavshevetTax == 1
    }

    @myName("hasMail")
    fun hasMail(): Boolean {
        return mailOrder == 1 || mailTaxNote == 1 || mailNote == 1
    }

    /** Any ERP / external-software send target (named integrations plus future oneNote/oneOrder/oneTax values). */
    @myName("hasExternalSoftware")
    fun hasExternalSoftware(): Boolean {
        return storeNextNote != 0 || storeNextTax != 0
                || managerOrder != 0 || managerNote != 0 || managerTax != 0
                || oneNote != 0 || oneOrder != 0 || oneTax != 0
                || hashavshevetNote != 0 || hashavshevetTax != 0
    }

    fun getZester(): ReadOrderJson? {
        return readOrderJson.firstOrNull { it.method == "zester" }
    }
}

@Serializable
data class ReadOrderJson(
    val type: String,
    val method: String,
    val username: String? = null,
    val password: String? = null,
    val token: String? = null,
    /** OData host, e.g. https://kleins.wee.co.il */
    val host: String? = null,
    /** Priority company folder under tabula.ini, e.g. kl00 */
    val odataCompany: String? = null,
    val idType:Int=0,
    val amount:Int = 0,
) {
    val realPassword = password?.replace("@", "'")?.replace("##", "@")
    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()
        map["type"] = JsonPrimitive(type.toString())
        map["method"] = JsonPrimitive(method.toString())
        username?.let {
            map["username"] = JsonPrimitive(it.toString())
        }
        password?.let {
            map["password"] = JsonPrimitive(it.toString())
        }
        token?.let {
            map["token"] = JsonPrimitive(it.toString())
        }
        host?.let {
            map["host"] = JsonPrimitive(it.toString())
        }
        odataCompany?.let {
            map["odataCompany"] = JsonPrimitive(it.toString())
        }
        return JsonObject(map)
    }

    companion object {
        fun createFromJson(js: String): List<ReadOrderJson> {
            return (getJsonWithConfig().parseToJsonElement((js).replace("\\'", "'")) as JsonArray).map {
                getJsonWithConfig().decodeFromJsonElement(ReadOrderJson.serializer(), it)
            }
        }

        fun toJsonArrayString(noteData: List<ReadOrderJson>): String {
            return if (noteData.isEmpty()) {
                "[]"
            } else {
                val payload = noteData.map {
                    getJsonWithConfig().encodeToJsonElement(JsonObject.serializer(), it.toJson()).toString()
                }.joinToString(",")
                return "[$payload]"
            }

        }
    }
}